import {groupBy, groupByUniqueKey} from '@benchling/js-utils';
import {Edge, MarkerType, Position, Node, XYPosition} from 'reactflow';

import {assert, assertNotNullAndReturn} from 'Asserts';

import {NodeType} from './UnitOperationFlowchartNode';
import {UnitOperationFields} from './useUnitOpLinkingState';

// Basis: reactflow internal coordinate space
export const FIRST_DEST_COLUMN_X = 0;
export const HIGHEST_DEST_NODE_Y = 0;
export const NODE_HORIZONTAL_GAP = 64;
export const NODE_VERTICAL_GAP = 50;
export const PROCEDURE_RUN_HORIZONTAL_GAP = 200; // Gap between procedure runs
export const PROCEDURE_RUN_VERTICAL_GAP = 100; // Minimum vertical gap between procedure runs in the same column

const UNIT_OP_LINK_EDGE_ID_PREFIX = 'unitop-link-edge-';
const INTRA_RUN_METHOD_EDGE_ID_PREFIX = 'intra-run-method-edge-';
export interface UnitOpLinkEdge {
  sourcePMEIId: string;
  destinationPMEIId: string;
}

export type SourcePMEINode = Node<UnitOperationFields, NodeType.SOURCE_PMEI>;
export type DestinationPMEINode = Node<UnitOperationFields, NodeType.DESTINATION_PMEI>;

export function getSourceNodeId(pmeiId: string): string {
  return `source-${pmeiId}`;
}

export function getDestinationNodeId(pmeiId: string): string {
  return `dest-${pmeiId}`;
}

export function getMethodId(procedureRunId: string, templateMethodId: string): string {
  return `${procedureRunId}-${templateMethodId}`;
}

/**
 * Used to make source PMEI nodes for the flowchart editor.
 * @param data - The data for the source PMEI node.
 * @returns a reactflow node representing a source PMEI.
 */
export function makeSourcePMEINode(data: UnitOperationFields): SourcePMEINode {
  return {
    id: getSourceNodeId(data.pmeiId),
    position: {x: 0, y: 0}, // position will be calculated in useLayoutEffect after dimensions are known
    data,
    type: NodeType.SOURCE_PMEI,
    selectable: false,
    deletable: false,
    draggable: false,
    connectable: false,
    sourcePosition: Position.Right,
  };
}

/**
 * Used to make destination PMEI nodes for the flowchart editor.
 * @param data - The data for the destination PMEI node.
 * @param nodeProps - Additional node props for the destination PMEI node.
 * @returns a reactflow node representing a destination PMEI.
 */
export function makeDestinationPMEINode(
  data: UnitOperationFields,
  nodeProps: {selectable: boolean}
): DestinationPMEINode {
  return {
    id: getDestinationNodeId(data.pmeiId),
    position: {x: 0, y: 0}, // position will be calculated in useLayoutEffect after dimensions are known
    data,
    type: NodeType.DESTINATION_PMEI,
    selectable: nodeProps.selectable,
    deletable: false,
    draggable: false,
    connectable: false,
    targetPosition: Position.Left,
  };
}

export function isUnitOpLinkEdgeId(edgeId: string): boolean {
  return edgeId.startsWith(UNIT_OP_LINK_EDGE_ID_PREFIX);
}

export function isIntraRunMethodEdgeId(edgeId: string): boolean {
  return edgeId.startsWith(INTRA_RUN_METHOD_EDGE_ID_PREFIX);
}

/**
 * Used to make unit operation link edges for the flowchart editor.
 * @param data - The data for the unit operation link edge.
 * @returns a reactflow edge representing a unit operation link.
 */
export function makeUnitOpLinkEdge(data: UnitOpLinkEdge): Edge<UnitOpLinkEdge> {
  return {
    id: `${UNIT_OP_LINK_EDGE_ID_PREFIX}${data.sourcePMEIId}-${data.destinationPMEIId}`,
    source: getSourceNodeId(data.sourcePMEIId),
    target: getDestinationNodeId(data.destinationPMEIId),
    type: 'default',
    data,
    deletable: false,
    animated: false,
  };
}

/**
 * Used to sort PMEIs for the flowchart editor. PMEIs are sorted by procedure run, method, condition, and replicate.
 */
export function sortComparePMEIs(dataA: UnitOperationFields, dataB: UnitOperationFields): number {
  return (
    dataA.procedureRunOrderKey - dataB.procedureRunOrderKey ||
    dataA.methodOrderKey - dataB.methodOrderKey ||
    dataA.conditionOrderKey - dataB.conditionOrderKey ||
    dataA.replicateNumber - dataB.replicateNumber
  );
}

/**
 * Used to group PMEIs by template method ID for the flowchart editor.
 * @param fullyOrderedDestPMEIs - The PMEIs to group.
 * @returns an array of arrays of PMEIs, grouped by template method ID.
 */
function getOrderedDestPMEIsInMethodColumns(
  fullyOrderedDestPMEIs: Array<UnitOperationFields>
): Array<Array<UnitOperationFields>> {
  const orderedDestTemplateMethodIds = [
    ...new Set(fullyOrderedDestPMEIs.map(({templateMethodId}) => templateMethodId)),
  ];
  const orderedDestPMEIsByTemplateMethodId = groupBy(
    fullyOrderedDestPMEIs,
    ({templateMethodId}) => templateMethodId
  );
  return orderedDestTemplateMethodIds.map((templateMethodId) =>
    assertNotNullAndReturn(orderedDestPMEIsByTemplateMethodId[templateMethodId])
  );
}

/**
 * @param destinationPMEIs - The destination PMEIs of a single procedure run.
 * @returns an array of arrays of destination PMEIs, grouped by template method ID.
 *
 * This is what the output looks like visually:
 * condition replicate name | pcmdv_123 | pcmdv_456 | pcmdv_789 |
 * conditon 1 - replicate 1 | pmei1         | pmei2         | pmei3
 * conditon 1 - replicate 2 | pmei4         | pmei5         | pmei6
 * conditon 2 - replicate 1 | pmei7         | pmei8         | pmei9
 *
 * The output shape in this example is:
 * [
 *    [pmei1, pmei4, pmei7], // method 1
 *    [pmei2, pmei5, pmei8], // method 2
 *    [pmei3, pmei6, pmei9], // method 3
 * ]
 */
export function getOrderedDestPMEIsInColumns(
  destinationPMEIs: Array<UnitOperationFields>
): Array<Array<UnitOperationFields>> {
  const fullyOrderedDestPMEIs = destinationPMEIs.toSorted(sortComparePMEIs);
  const orderedDestPMEIsInMethodColumns = getOrderedDestPMEIsInMethodColumns(fullyOrderedDestPMEIs);
  return orderedDestPMEIsInMethodColumns;
}

/**
 * @returns a 3D array of destination PMEIs, grouped by method name for each procedure run.
 *
 * An example output shape is:
 * [
 *    [ // procedure run 1
 *      [pmei1, pmei4, pmei7], // method 1
 *      [pmei2, pmei5, pmei8], // method 2
 *      [pmei3, pmei6, pmei9], // method 3
 *    ],
 *    [ // procedure run 2
 *      [pmei1, pmei5, pmei9],  // method 1
 *      [pmei2, pmei6, pmei10], // method 2
 *      [pmei3, pmei7, pmei11], // method 3
 *      [pmei4, pmei8, pmei12], // method 4
 *    ],
 * ]
 */
export function getOrderedDestPMEIsInColumnsPerProcedureRun(
  destinationPMEIs: Array<UnitOperationFields>
): Array<Array<Array<UnitOperationFields>>> {
  const fullyOrderedDestPMEIs = destinationPMEIs.toSorted(sortComparePMEIs);
  const orderedProcedureRunIds = [
    ...new Set(fullyOrderedDestPMEIs.map(({procedureRunId}) => procedureRunId)),
  ];
  const orderedDestPMEIsByProcedureRunId = groupBy(
    fullyOrderedDestPMEIs,
    ({procedureRunId}) => procedureRunId
  );
  return orderedProcedureRunIds.map((procedureRunId) =>
    getOrderedDestPMEIsInMethodColumns(
      assertNotNullAndReturn(orderedDestPMEIsByProcedureRunId[procedureRunId])
    )
  );
}

/**
 * @returns reactflow edges representing intra-run method edges in the destination run.
 * We assume intra-run split pooling does not exist yet, so we can derive intra-run method edges by zipping
 * destination PMEIs in adjacent methods.
 */
export function makeIntraRunMethodEdges(
  orderedDestPMEIsInColumns: Array<Array<UnitOperationFields>>
): Array<Edge<UnitOpLinkEdge>> {
  return orderedDestPMEIsInColumns.slice(1).flatMap((currentColumn, i) => {
    const prevColumn = orderedDestPMEIsInColumns[i];
    assert(
      prevColumn.length === currentColumn.length,
      'Varying number of PMEIs across methods are not supported.'
    );
    return currentColumn.map((currentPMEI, j) => {
      const prevPMEI = prevColumn[j];
      assert(
        prevPMEI.conditionName === currentPMEI.conditionName &&
          prevPMEI.replicateNumber === currentPMEI.replicateNumber,
        'Expected existing and current methods to have same condition/replicate setup'
      );
      return {
        id: `${INTRA_RUN_METHOD_EDGE_ID_PREFIX}${prevPMEI.pmeiId}-${currentPMEI.pmeiId}`,
        source: getDestinationNodeId(prevPMEI.pmeiId),
        target: getDestinationNodeId(currentPMEI.pmeiId),
        type: 'default',
        data: {
          sourcePMEIId: prevPMEI.pmeiId,
          destinationPMEIId: currentPMEI.pmeiId,
        },
        deletable: false,
        animated: false,
        markerEnd: {type: MarkerType.ArrowClosed},
      };
    });
  });
}

/**
 * Compute positions for destination nodes within a single procedure run.
 * Each method gets its own vertical column, with nodes ordered by condition and replicate.
 * @param destNodes - Array of destination nodes
 * @param orderedPMEIsInColumns - PMEIs grouped by method columns
 * @param startingX - The starting X coordinate for this procedure run
 * @returns Record mapping PMEI IDs to their positions and the height of the tallest column.
 *
 * Visual example output:
 * condition 1 - replicate 1 | step1 | step2 | step3
 * condition 1 - replicate 2 | step1 | step2 | step3
 * condition 2 - replicate 1 | step1 | step2 | step3
 * condition 2 - replicate 2 | step1 | step2 | step3
 */
export function getNodePositionsByPMEIIdForProcedureRun(
  destNodes: Array<Node>,
  orderedDestPMEIsInColumns: Array<Array<UnitOperationFields>>,
  startingX: number = FIRST_DEST_COLUMN_X
): {positionsByPMEIId: Record<string, XYPosition>; maxDestColumnHeight: number} {
  assert(
    new Set(destNodes.map(({data}) => data.procedureRunName)).size === 1,
    'Only destination PMEIs from one run is supported because we rely on method name uniqueness for column layout'
  );
  // Figure out which dest nodes go in which dest node column.
  const destNodeByPMEIId = groupByUniqueKey(destNodes, ({data: {pmeiId}}) => pmeiId);
  const orderedDestNodesInColumns = orderedDestPMEIsInColumns.map((pmeis) =>
    pmeis.map(({pmeiId}) => destNodeByPMEIId[pmeiId])
  );

  // Figure out Y-offset of each column for vertical centering.
  const destColumnHeights = orderedDestNodesInColumns.map((nodes) => getHeightOfNodeColumn(nodes));
  const maxDestColumnHeight = Math.max(...destColumnHeights);
  const destColumnYOffsets = destColumnHeights.map((height) => (maxDestColumnHeight - height) / 2);

  // Node widths are not fixed, so we need to dynamically calculate the X-offset for each column.
  const destColumnCumulativeXOffsets = orderedDestNodesInColumns.reduce((offsets, nodes, i) => {
    if (i === 0) {
      offsets.push(startingX);
      return offsets;
    }
    const prevOffset = offsets[i - 1];
    const maxNodeWidth = Math.max(...orderedDestNodesInColumns[i - 1].map((node) => node.width ?? 0));
    const nextOffset = prevOffset + maxNodeWidth + NODE_HORIZONTAL_GAP;
    offsets.push(nextOffset);
    return offsets;
  }, [] as Array<number>);

  // Calculate positions for each node.
  const positionsByPMEIId: Record<string, XYPosition> = {};
  orderedDestNodesInColumns.forEach((nodes, columnIndex) => {
    nodes.forEach((node, nodeIndex) => {
      if (nodeIndex === 0) {
        positionsByPMEIId[node.data.pmeiId] = {
          x: destColumnCumulativeXOffsets[columnIndex],
          y: HIGHEST_DEST_NODE_Y + destColumnYOffsets[columnIndex],
        };
      } else {
        const prevNode = nodes[nodeIndex - 1];
        positionsByPMEIId[node.data.pmeiId] = {
          x: destColumnCumulativeXOffsets[columnIndex],
          // Node heights are not fixed, calc Y position based on existing node Y position and height.
          y: positionsByPMEIId[prevNode.data.pmeiId].y + (prevNode.height ?? 0) + NODE_VERTICAL_GAP,
        };
      }
    });
  });
  return {positionsByPMEIId, maxDestColumnHeight};
}

/**
 * Used to calculate the height of a node column.
 * @param nodesInColumn - The nodes in the column.
 * @returns the height of the column.
 */
export function getHeightOfNodeColumn(nodesInColumn: Array<Node>): number {
  const nodeHeights = nodesInColumn.reduce((nodeHeights, node) => nodeHeights + (node.height ?? 0), 0);
  return nodeHeights + Math.max(0, nodesInColumn.length - 1) * NODE_VERTICAL_GAP;
}
